import type { CriteriaState } from '../types';
import type { CriteriaSectionDef, CriteriaTabKey, SectionContext } from './types';
import { USERS_SECTIONS } from './users';
import { ATTRIBUTE_SECTIONS } from './attributes';
import { QUALITY_SCORE_SECTIONS } from './qualityScore';
import { PRE_PING_SECTIONS } from './prePing';
import { CAPS_SECTIONS } from './caps';
import { ANALYSIS_SECTIONS } from './analysis';

export type { CriteriaSectionDef, CriteriaTabKey, SectionContext } from './types';

interface CriteriaTabDef {
  key: CriteriaTabKey;
  title: string;
  isAvailable?: (ctx: SectionContext) => boolean;
}

/** The editing tabs, in display order. The Summary tab sits in front of these. */
export const CRITERIA_TABS: CriteriaTabDef[] = [
  { key: 'users', title: 'Users' },
  { key: 'attribute', title: 'Attribute' },
  { key: 'affiliateQualityScore', title: 'Affiliate Quality Score' },
  { key: 'preping', title: 'Pre-ping' },
  { key: 'daily', title: 'Caps & Restrictions', isAvailable: (ctx) => !ctx.isPublisher && !ctx.hideCapsTab },
  { key: 'analysis', title: 'Analysis', isAvailable: (ctx) => !ctx.isPublisher },
];

/** Every section, in the order the tabs and the Summary render them. */
export const CRITERIA_SECTIONS: CriteriaSectionDef[] = [
  ...USERS_SECTIONS,
  ...ATTRIBUTE_SECTIONS,
  ...QUALITY_SCORE_SECTIONS,
  ...PRE_PING_SECTIONS,
  ...CAPS_SECTIONS,
  ...ANALYSIS_SECTIONS,
];

export function availableTabs(ctx: SectionContext): CriteriaTabDef[] {
  return CRITERIA_TABS.filter((tab) => tab.isAvailable?.(ctx) ?? true);
}

/** The sections that apply to this panel — on an available tab, and for this source type. */
export function availableSections(ctx: SectionContext): CriteriaSectionDef[] {
  const tabs = new Set(availableTabs(ctx).map((tab) => tab.key));
  return CRITERIA_SECTIONS.filter((section) => tabs.has(section.tab) && (section.appliesTo?.(ctx) ?? true));
}

export function isSectionConfigured(section: CriteriaSectionDef, state: CriteriaState, ctx: SectionContext): boolean {
  return Boolean(section.isConfigured(state, ctx));
}

export interface SectionSearchResult {
  section: CriteriaSectionDef;
  /** The field or alias that matched, when it wasn't the title. */
  matched?: string;
}

/**
 * Ranks the available sections against a query: title matches first, then visible field
 * labels, then aliases. Every word of the query has to appear somewhere in the section.
 */
export function searchSections(query: string, ctx: SectionContext): SectionSearchResult[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];

  const ranked: { result: SectionSearchResult; rank: number; order: number }[] = [];
  availableSections(ctx).forEach((section, order) => {
    const haystack = [section.title, ...section.fields, ...section.aliases].join(' ').toLowerCase();
    if (!words.every((word) => haystack.includes(word))) return;

    const q = words.join(' ');
    const hit = (term: string) => term.toLowerCase().includes(q);
    if (hit(section.title)) {
      ranked.push({ result: { section }, rank: section.title.toLowerCase().startsWith(q) ? 0 : 1, order });
      return;
    }
    const field = section.fields.find(hit);
    const alias = field ? undefined : section.aliases.find(hit);
    ranked.push({ result: { section, matched: field ?? alias }, rank: field ? 2 : alias ? 3 : 4, order });
  });

  return ranked.sort((a, b) => a.rank - b.rank || a.order - b.order).map(({ result }) => result);
}
